import { useContext, useEffect, useState } from 'react';

import {
  Block,
  Button,
  DateCellComponent,
  FlexContainer,
  FullscreenSheet,
  Loader,
  SoundNotificationContext,
  Span,
  TableSimple,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';

import { getAcceptanceActBags, getNotAcceptedManifestBags } from '../api';
import { AcceptanceActBagsHttpResponse, NotAcceptedManifestBagsHttpResponse } from '../model/manifestAcceptance';
import './AcceptanceScanning.scss';

type Props = {
  acceptanceActId: number | null;
  onClose: () => void;
};

type BagRow = {
  id: string;
  number: string;
  kind: string;
  acceptedBy: string;
  acceptanceDate: string;
  isUndeclared: boolean;
};

const UNDECLARED_COLOR = '#8a5a00';

/** Карточка закрытой приёмки: принятые мешки (заявленные и незаявленные) и, если был манифест, что из него не приехало */
export default function AcceptanceActDetails({ acceptanceActId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { createNotification } = useNotificationsV2();
  const { onSoundError } = useContext(SoundNotificationContext);
  const [details, setDetails] = useState<AcceptanceActBagsHttpResponse | null>(null);
  const [notAccepted, setNotAccepted] = useState<NotAcceptedManifestBagsHttpResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (acceptanceActId === null) {
      setDetails(null);
      setNotAccepted(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const act = await getAcceptanceActBags(acceptanceActId);
        if (cancelled) return;
        setDetails(act);

        // Незаявленных в манифесте нет по определению, а вот заявленные, но не приехавшие — есть.
        if (act.manifestNumber) {
          const missing = await getNotAcceptedManifestBags(act.manifestNumber);
          if (!cancelled) setNotAccepted(missing);
        } else {
          setNotAccepted(null);
        }
      } catch {
        if (cancelled) return;
        createNotification({
          type: 'error',
          message: {
            title: t('common.systemError.title'),
            desc: t('common.systemError.desc'),
          },
        });
        onSoundError?.();
        onClose();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptanceActId]);

  const rows: BagRow[] = (details?.bags ?? []).map((bag, index) => ({
    id: `${bag.number}-${index}`,
    number: bag.number,
    kind: bag.isUndeclared ? t('manifestAcceptance.scan.kindUndeclared') : t('manifestAcceptance.scan.kindDeclared'),
    acceptedBy: bag.acceptedBy?.login ?? '-',
    acceptanceDate: bag.acceptanceDate ?? '-',
    isUndeclared: !!bag.isUndeclared,
  }));

  const undeclaredCount = rows.filter((row) => row.isUndeclared).length;

  return (
    <FullscreenSheet height={'100dvh'} open={acceptanceActId !== null} onClose={onClose}>
      <div className="manifest-scan-header">
        <Block className="manifest-scan-header__title">
          <FlexContainer direction="column" gap="2px">
            <Span fw={600}>
              {t('manifestAcceptance.acceptanceActs.details.title')} {details?.number ?? ''}
            </Span>
            <Span>
              {t('tableHeader.manifestNumber')}:{' '}
              {details?.manifestNumber ?? t('manifestAcceptance.acceptanceActs.withoutManifest')}
            </Span>
          </FlexContainer>
        </Block>
        <div className="manifest-scan-header__actions">
          <Button onClick={onClose} width="120px">
            {t('tableHeader.rollUp')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <FlexContainer align="center" justify="center" style={{ padding: 32 }}>
          <Loader size="s" />
        </FlexContainer>
      ) : (
        <>
          <Block mt={16} mb={16}>
            <FlexContainer direction="column" gap="6px">
              <Span>
                {t('tableHeader.vehicleNumber')}: {details?.vehicleNumber ?? '-'}
              </Span>
              <Span>
                {t('manifestAcceptance.acceptanceActs.openedBy')}: {details?.openedByLogin ?? '-'}
              </Span>
              <Span>
                {t('tableHeader.createdAt')}:{' '}
                {details?.createdAt ? (
                  <DateCellComponent justify="flex-start" date={details.createdAt} zone="local" />
                ) : (
                  '-'
                )}
              </Span>
              <Span>
                {t('manifestAcceptance.acceptanceActs.closeAt')}:{' '}
                {details?.closeAt ? (
                  <DateCellComponent justify="flex-start" date={details.closeAt} zone="local" />
                ) : (
                  '-'
                )}
              </Span>
              <Span fw={600}>
                {t('manifestAcceptance.acceptanceActs.details.acceptedTitle', {
                  total: rows.length,
                  undeclared: undeclaredCount,
                })}
              </Span>
            </FlexContainer>
          </Block>

          <TableSimple<BagRow>
            enableColumnResizing={false}
            columns={[
              {
                header: t('tableHeader.bagNumber'),
                accessor: 'number',
                textAlign: 'center',
                renderCell: (row) =>
                  row.isUndeclared ? (
                    <span style={{ color: UNDECLARED_COLOR, fontWeight: 600 }}>{row.number}</span>
                  ) : (
                    row.number
                  ),
              },
              {
                header: t('tableHeader.status'),
                accessor: 'kind',
                textAlign: 'center',
                renderCell: (row) =>
                  row.isUndeclared ? (
                    <span style={{ color: UNDECLARED_COLOR, fontWeight: 600 }}>{row.kind}</span>
                  ) : (
                    row.kind
                  ),
              },
              { header: t('tableHeader.userName'), accessor: 'acceptedBy', textAlign: 'center' },
              {
                header: t('manifestAcceptance.details.acceptanceDate'),
                accessor: 'acceptanceDate',
                textAlign: 'center',
                renderCell: (row) =>
                  row.acceptanceDate === '-' ? (
                    '-'
                  ) : (
                    <DateCellComponent justify="center" date={row.acceptanceDate} zone="local" />
                  ),
              },
            ]}
            data={rows}
            language={i18n.language}
            withOutPagination
            mobileWidth={680}
            maxHeightTable={notAccepted ? 'calc(50vh - 160px)' : 'calc(100vh - 300px)'}
            isLoading={false}
            emptyPlaceholder={t('noData')}
          />

          {notAccepted && (
            <Block mt={16}>
              <Block mb={8}>
                <Span fw={600}>
                  {t('manifestAcceptance.acceptanceActs.details.notAcceptedTitle', {
                    count: notAccepted.notAcceptedCount,
                    total: notAccepted.totalCount,
                  })}
                </Span>
              </Block>
              <TableSimple<{ id: string; number: string }>
                enableColumnResizing={false}
                columns={[{ header: t('tableHeader.bagNumber'), accessor: 'number', textAlign: 'center' }]}
                data={notAccepted.bags.map((bag, index) => ({ id: `${bag.number}-${index}`, number: bag.number }))}
                language={i18n.language}
                withOutPagination
                mobileWidth={680}
                maxHeightTable="calc(50vh - 160px)"
                isLoading={false}
                emptyPlaceholder={t('manifestAcceptance.notAccepted.empty')}
              />
            </Block>
          )}
        </>
      )}
    </FullscreenSheet>
  );
}
